<template>
  <div class="streaming-summary-container pa-4" :style="summaryContainerStyle">
    <div class="close-button-container">
      <slot name="close-button"></slot>
    </div>
    
    <!-- Añadir slot para los chips de estructura -->
    <div class="structure-chips-wrapper">
      <slot name="structure-chips"></slot>
    </div>
    
    <div v-if="isLoadingInitial" class="text-center my-6">
      <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
      <p class="mt-3 text-caption grey--text">Generando resumen general del documento...</p>
    </div>
    <div v-else-if="streamError" class="text-center my-6">
      <v-alert type="error" outlined prominent>
        <div class="font-weight-medium mb-2">Error al generar el resumen:</div>
        {{ streamError }}
      </v-alert>
      <v-btn color="primary" outlined @click="fetchStreamingSummary" class="mt-4">Reintentar</v-btn>
      <v-btn text @click="proceedAnyway" class="mt-4 ml-2">Continuar sin resumen</v-btn>
    </div>
    <div v-else class="summary-content">
      <div v-if="!accumulatedSummaryContent && !isStreaming" class="text-center grey--text py-5">
        No se ha podido generar un resumen para este documento.
      </div>
      <div v-else-if="accumulatedSummaryContent" v-html="renderMarkdown(accumulatedSummaryContent)" class="markdown-render"></div>
      <div v-if="isStreaming && !accumulatedSummaryContent" class="text-center grey--text py-5">
          <v-progress-circular indeterminate color="grey" size="24"></v-progress-circular>
          <span class="ml-2">Recibiendo resumen...</span>
      </div>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked';
import DOMPurify from 'dompurify';

export default {
  name: 'StreamingSummaryDisplay',
  props: {
    documentId: {
      type: [String, Number],
      required: true
    },
    documentName: {
      type: String,
      required: true
    },
    language: {
      type: String,
      default: 'auto'
    }
    // userId no es necesario como prop si usamos this.$root.currentUser.userId
  },
  data() {
    return {
      accumulatedSummaryContent: '',
      isStreaming: false,
      isLoadingInitial: false,
      streamError: null,
      abortController: null,
      isContentHidden: false,
    };
  },
  computed: {
    summaryContainerStyle() {
      return {
        opacity: this.isContentHidden ? '0' : (this.isStreaming || this.accumulatedSummaryContent ? '1' : '0'),
        zIndex: this.isContentHidden ? '-1' : (this.isStreaming || this.accumulatedSummaryContent ? '9999' : '-1'),
        position: 'fixed',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        transition: 'opacity 0.3s ease-in-out',
        background: 'rgba(255, 255, 255)',
        overflow: 'auto'
      };
    }
  },
  beforeDestroy() {
    if (this.abortController) {
      this.abortController.abort('Componente destruido');
    }
  },
  methods: {
    renderMarkdown(rawMarkdown) {
      if (!rawMarkdown) return '';
      const rawHtml = marked(rawMarkdown, { breaks: true, gfm: true });
      let cleanHtml = DOMPurify.sanitize(rawHtml);
      cleanHtml = cleanHtml.replace(/<table>/g, '<table class="md-table">');
      return cleanHtml;
    },
    async fetchStreamingSummary() {
      console.log("StreamingSummaryDisplay: Iniciando fetchStreamingSummary(). documentId:", this.documentId);
      this.accumulatedSummaryContent = '';
      this.streamError = null;
      this.isStreaming = true;
      this.isContentHidden = false;

      if (!this.documentId) {
        this.streamError = "No se proporcionó un ID de documento.";
        this.isStreaming = false;
        this.$emit('summary-error-internal', this.streamError);
        return Promise.resolve(null);
      }

      if (this.abortController) {
        this.abortController.abort('Nueva petición de summary iniciada');
      }
      this.abortController = new AbortController();

      return new Promise(async (resolve, reject) => {
        try {
          const backendUrl = process.env.VUE_APP_AXIOS_URI || '';
          const apiUrl = `${backendUrl}/chat/generate-document-summary`;

          console.log(`StreamingSummaryDisplay: Realizando fetch a ${apiUrl} con documentId: ${this.documentId}, language: ${this.language}`);

          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'text/event-stream',
              Authorization: this.$root.token?.value || ''
            },
            body: JSON.stringify({
              documentId: this.documentId,
              userId: this.$root.currentUser?.userId,
              language: this.language
            }),
            signal: this.abortController.signal
          });

          if (!response.ok) {
            let errorMsg = `Error HTTP: ${response.status} ${response.statusText}`;
            let errorDetails = null;
            try {
              const errorBody = await response.json();
              errorMsg = errorBody.error || errorBody.message || errorMsg;
              errorDetails = errorBody.details;
            } catch (e) { /* Ignorar */ }
            
            this.handleStreamErrorLocal(errorMsg, errorDetails);
            resolve(null);
            return;
          }

          if (!response.body) {
            this.handleStreamErrorLocal("El cuerpo de la respuesta es nulo.");
            resolve(null);
            return;
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              this.handleStreamEndLocal();
              resolve(this.accumulatedSummaryContent);
              break;
            }

            buffer += decoder.decode(value, { stream: true });
            let eolIndex;
            while ((eolIndex = buffer.indexOf('\n')) >= 0) {
              const line = buffer.substring(0, eolIndex).trim();
              buffer = buffer.substring(eolIndex + 1);

              if (line.startsWith('data:')) {
                const jsonData = line.substring(5).trim();
                try {
                  const data = JSON.parse(jsonData);
                  if (data.text) {
                    this.accumulatedSummaryContent += data.text;
                  }
                } catch (e) {
                  console.error("Error al parsear datos SSE:", e, "JSON bruto:", jsonData);
                }
              } else if (line.startsWith('event: error_event')) {
                  const errorJsonData = buffer.substring(buffer.indexOf('data:') + 5, buffer.indexOf('\n\n')).trim();
                  let errorData = { error: "Error desconocido durante el streaming." };
                   try {
                      errorData = JSON.parse(errorJsonData);
                   } catch(e) { console.error("Error al parsear datos del evento de error:", e); }
                   this.handleStreamErrorLocal(errorData.error, errorData.details);
                   if (this.abortController) this.abortController.abort('Error event recibido');
                   resolve(null);
                   return; 
              } else if (line.startsWith('event: end')) {
                  // Podríamos llamar a handleStreamEndLocal aquí si el backend lo garantiza antes del done
              }
            }
          }
        } catch (error) {
          if (error.name === 'AbortError') {
            console.log("Streaming del resumen abortado por AbortController.");
            this.handleStreamErrorLocal('Generación de resumen cancelada', 'ABORTED');
            resolve(null);
          } else {
            console.error('Error durante el streaming del resumen (catch general):', error);
            this.handleStreamErrorLocal('Error de conexión o procesamiento del stream.');
            resolve(null);
          }
        }
      });
    },
    handleStreamErrorLocal(errorMessage, errorDetails = null) {
      console.error("StreamingSummaryDisplay: Error en handleStreamErrorLocal. errorMessage:", errorMessage, "errorDetails:", errorDetails);
      this.isStreaming = false;
      const displayMessage = errorDetails === 'SAFETY_BLOCK'
        ? 'El resumen no se puede mostrar debido a políticas de seguridad.'
        : errorMessage || 'Ocurrió un error desconocido al generar el resumen.';
      this.streamError = displayMessage;
      this.$emit('summary-display-error', displayMessage);
    },
    handleStreamEndLocal() {
      console.log("StreamingSummaryDisplay: Stream finalizado localmente.");
      this.isStreaming = false;
      if (!this.accumulatedSummaryContent && !this.streamError) {
         this.streamError = "No se recibió contenido para el resumen.";
      }
      this.$emit('summary-display-complete', this.accumulatedSummaryContent);
    },
    proceedAnyway() {
        this.$emit('summary-display-skipped-by-user'); 
    },
    hideContent() {
      this.isContentHidden = true;
    }
  }
};
</script>

<style scoped>
.streaming-summary-container {
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  background-color: #f9f9f9;
  border-radius: 0;
  padding: 40px !important;
}

.close-button-container {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10001;
}

/* Añadir estilos para el contenedor de chips de estructura */
.structure-chips-wrapper {
  margin-bottom: 20px;
  margin-top: 10px;
  width: 100%;
  text-align: center;
}

.summary-content {
  max-width: 800px;
  margin: 0 auto;
  min-height: 150px;
  padding-top: 20px;
}

.markdown-render {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
  line-height: 1.6;
  color: #333;
  padding: 20px 30px;
  background-color: white;
  border-radius: 8px;
  border: 1px solid #eee;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

/* Estilos básicos para el markdown */
.markdown-render >>> h1,
.markdown-render >>> h2,
.markdown-render >>> h3 {
  margin-top: 1em;
  margin-bottom: 0.5em;
  font-weight: 600;
}
.markdown-render >>> p {
  margin-bottom: 0.8em;
}
.markdown-render >>> ul,
.markdown-render >>> ol {
  padding-left: 1.5em;
  margin-bottom: 0.8em;
}
.markdown-render >>> code {
  background-color: #f0f0f0;
  padding: 0.2em 0.4em;
  border-radius: 3px;
  font-size: 90%;
}
.markdown-render >>> pre {
  background-color: #f0f0f0;
  padding: 10px;
  border-radius: 4px;
  overflow-x: auto;
}
.markdown-render >>> blockquote {
  border-left: 3px solid #ccc;
  padding-left: 1em;
  margin-left: 0;
  font-style: italic;
  color: #555;
}
.markdown-render >>> table.md-table {
  border-collapse: collapse;
  width: 100%;
  margin: 1em 0;
}
.markdown-render >>> table.md-table th,
.markdown-render >>> table.md-table td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}
.markdown-render >>> table.md-table th {
  background-color: #f2f2f2;
}

.v-alert {
    text-align: left;
}
</style> 